import type {
  FieldError,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';

import type { FormFieldError } from '../types';

/**
 * Извлекает сообщение об ошибке из объекта FieldError.
 *
 * @example
 * getErrorMessage(errors?.name)
 * getErrorMessage(errors?.featureFlags?.autoArchive)
 */
export function getErrorMessage(
  error: FieldError | undefined | null,
): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const msg = error.message;

  return typeof msg === 'string' ? msg : undefined;
}

/**
 * Извлекает сообщение об ошибке в виде массива для компонентов,
 * ожидающих error?: string[] (FormEditor, FormImageUploader).
 */
export function getErrorAsArray(
  error: FieldError | undefined | null,
): string[] | undefined {
  const msg = getErrorMessage(error);

  return msg ? [msg] : undefined;
}

/**
 * Возвращает первое сообщение об ошибке из объекта FieldErrors формы.
 */
export function getFirstFormErrorMessage(
  errors: FieldErrors<FieldValues>,
): string | undefined {
  for (const value of Object.values(errors)) {
    if (!value || typeof value !== 'object') {
      continue;
    }

    const message = getErrorMessage(value as FieldError);

    if (message) {
      return message;
    }

    const nested = getFirstFormErrorMessage(value as FieldErrors<FieldValues>);

    if (nested) {
      return nested;
    }
  }

  return undefined;
}

export function getFormFieldError(error: unknown): FormFieldError {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('message' in error) {
    return getErrorMessage(error as FieldError);
  }

  return getFirstFormErrorMessage(error as FieldErrors<FieldValues>);
}

/**
 * Устанавливает ошибки валидации с сервера в форму.
 */
export function setFormServerErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  errors: Record<string, string[]>,
): void {
  for (const [key, messages] of Object.entries(errors)) {
    const message = Array.isArray(messages) ? messages[0] : messages;

    if (message) {
      form.setError(key as FieldPath<T>, { message, type: 'server' });
    }
  }
}

/** @deprecated Используйте setFormServerErrors */
export const handleFormServerErrors = setFormServerErrors;
